// ════════════════════════════════════════════════════════════════════
// i18n SCAFFOLDING (Arabic) — NEW file, framework only.
//
// Goal: give the web app a single, dependency-free place to (a) look up a
// UI string by stable key, (b) read/write the active language, and (c) tell
// the DOM which text direction to use (Arabic = RTL).
//
// HOW IT WORKS
//   • Active language lives in localStorage under "netrev_lang" (default "en").
//   • t(key) returns catalog[lang][key], falling back to catalog.en[key],
//     finally to the key itself — so a missing translation NEVER blanks the UI.
//   • useT() is a tiny React hook: it returns a bound t() and re-renders the
//     component when the language changes (via the "netrev:langchange" event
//     and the browser "storage" event for cross-tab sync).
//   • setLang(lang) persists the choice and broadcasts the change.
//   • dir()/isRTL() drive the document's dir attribute and any per-element
//     RTL handling.
//
// TRANSLATION STATUS: the first-batch keys below are translated to Modern
//    Standard Arabic (see arOverrides). Any FUTURE en key added without an
//    override falls back to a visible "TODO[ar]" marker (never blank), and the
//    runtime chain still resolves ar → en → key so the UI never breaks.
//
// This file ONLY provides the machinery + a first batch of high-traffic keys.
// Wiring App.js to actually call t() is a separate pass (see the WIRING SPEC
// returned to the orchestrator). No existing file is modified by adding this.
// ════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback } from "react";

// Per-page Arabic catalog modules (full-translation pass). Each exports
// { en, ar } with namespaced keys (landing.*, overview.*, …); merged below.
import { en as landingEn, ar as landingAr } from "./i18n-catalog/landing";
import { en as storyEn, ar as storyAr } from "./i18n-catalog/story";
import { en as overviewEn, ar as overviewAr } from "./i18n-catalog/overview";
import { en as analysisEn, ar as analysisAr } from "./i18n-catalog/analysis";
import { en as dailylogEn, ar as dailylogAr } from "./i18n-catalog/dailylog";
import { en as configEn, ar as configAr } from "./i18n-catalog/config";
import { en as subsEn, ar as subsAr } from "./i18n-catalog/subs";
import { en as perfEn, ar as perfAr } from "./i18n-catalog/perf";
import { en as pricetoolEn, ar as pricetoolAr } from "./i18n-catalog/pricetool";
import { en as appEn, ar as appAr } from "./i18n-catalog/app";
import { en as feedEn, ar as feedAr } from "./i18n-catalog/feed";

export const LANG_KEY = "netrev_lang";
export const DEFAULT_LANG = "en";
export const SUPPORTED_LANGS = ["en", "ar"];
export const LANG_CHANGE_EVENT = "netrev:langchange";

// Build an Arabic placeholder so untranslated keys are obvious, never blank.
const todo = (en) => `TODO[ar]: ${en}`;

// ── EN catalog: high-traffic UI strings, values copied VERBATIM from App.js.
// Keys are namespaced by area (nav.*, auth.*, btn.*, mobnav.*, …) and are the
// stable contract the UI references — do not rename without updating callers.
const en = {
  // ── Admin sidebar nav (App.js ADMIN_NAV ~3153) ───────────────────────
  "nav.overview": "Overview",
  "nav.analysis": "Insight+",
  "nav.pricetool": "Price Tool",
  "nav.dailylog": "Daily Log",
  "nav.config": "Configuration",
  "nav.subscriptions": "Subscriptions",
  "nav.team": "Team",
  // Account section label above Team/Subscriptions (App.js ~3383)
  "nav.section.account": "Account",

  // ── Super-admin sidebar nav (App.js SUPER_ADMIN_NAV ~3164) ───────────
  "nav.admin.overview": "Overview",
  "nav.admin.accounts": "Accounts",
  "nav.admin.audit": "Audit log",
  "nav.admin.superAdmins": "Super admins",
  // Admin section label above super-admin nav (App.js ~3334)
  "nav.section.admin": "Admin",

  // ── Mobile bottom tabs (App.js MOB_TABS ~5173) ───────────────────────
  "mobnav.dash": "Overview",
  "mobnav.items": "Items",
  "mobnav.log": "Log",
  "mobnav.account": "Account",

  // ── Top-bar / portal chrome ──────────────────────────────────────────
  "topbar.adminPortal": "Admin Portal",       // App.js ~5242
  "topbar.currentWorkspace": "Current workspace", // App.js ~5316
  // Mobile hamburger aria-label (portal top bar, <768px only)
  "topbar.openMenu": "Open menu",
  "topbar.closeMenu": "Close menu",

  // ── Page / section headings (auth screen, App.js ~844) ───────────────
  "auth.heading.signup": "Create your account",
  "auth.heading.verifyPending": "Verify your email",
  "auth.heading.reset": "Reset your password",
  "auth.heading.resetSent": "Check your email",
  "auth.heading.login": "Welcome back",
  // Subheadings (App.js ~847)
  "auth.sub.signup": "Sign up to access your dashboard and start tracking real profit.",
  "auth.sub.reset": "Enter the email address associated with your account and we'll send a reset link.",
  "auth.sub.login": "Sign in to access your dashboard and continue managing your restaurant.",
  // Field labels (App.js ~917,934,949,955,966,970)
  "auth.field.region": "Region",
  "auth.field.name": "Name",
  "auth.field.whatsapp": "Your number",
  "auth.field.businessType": "Business type",
  "auth.field.email": "Email Address",
  "auth.field.password": "Password",
  // Misc auth copy (App.js ~824,976,978,839)
  "auth.alreadyHaveAccount": "Already have an account? ",
  "auth.newToNetrev": "New to Netrev? ",
  "auth.keepSignedIn": "Keep me signed in",
  "auth.resetPassword": "Reset password",
  "auth.backToHome": "Back to home",
  "auth.backToSignIn": "Back to sign in",

  // ── Primary buttons (verbatim, App.js + shared UI) ───────────────────
  "btn.signIn": "Sign In",            // App.js ~981 (login submit) / "Sign in" link ~827
  "btn.createAccount": "Create account", // App.js ~827,981
  "btn.signOut": "Sign out",          // App.js ~5283, ~4511
  "btn.sendResetLink": "Send reset link", // App.js ~921
  "btn.save": "Save",                 // App.js ~410
  "btn.saveItem": "Save item",        // App.js ~284
  "btn.cancel": "Cancel",             // App.js ~284,411,532 (and many more)
  "btn.continue": "Continue →",       // App.js ~531
  "btn.confirmPay": "Confirm & pay via Stripe", // App.js ~547
  "btn.upgradePro": "Upgrade to Pro — £49.99/mo", // App.js ~1036
  "btn.pleaseWait": "Please wait…",   // App.js ~981 (loading state)
  "btn.sending": "Sending…",          // App.js ~921 (loading state)

  // ── Loading / status copy ────────────────────────────────────────────
  "status.settingUpAccount": "Setting up your account…", // App.js ~5294
  "status.settingUpSub": "Just a moment while we get your workspace ready.", // ~5295
  "status.loadingWorkspaces": "Loading your workspaces...", // App.js ~5347

  // ── Plan labels (App.js ~621) ────────────────────────────────────────
  "plan.free": "Free",
  "plan.pro": "Pro",
};

// ── AR catalog: Modern Standard Arabic translations of the EN keys. ──────
// Every current key is translated; the Object.fromEntries below keeps the
// todo() fallback only for any FUTURE en key that lacks an override.
const arOverrides = {
  // Admin sidebar nav
  "nav.overview": "نظرة عامة",
  "nav.analysis": "رؤى+",
  "nav.pricetool": "أداة التسعير",
  "nav.dailylog": "السجل اليومي",
  "nav.config": "الإعدادات",
  "nav.subscriptions": "الاشتراكات",
  "nav.team": "الفريق",
  "nav.section.account": "الحساب",
  // Super-admin sidebar nav
  "nav.admin.overview": "نظرة عامة",
  "nav.admin.accounts": "الحسابات",
  "nav.admin.audit": "سجل التدقيق",
  "nav.admin.superAdmins": "كبار المسؤولين",
  "nav.section.admin": "الإدارة",
  // Mobile bottom tabs
  "mobnav.dash": "نظرة عامة",
  "mobnav.items": "الأصناف",
  "mobnav.log": "السجل",
  "mobnav.account": "الحساب",
  // Top-bar / portal chrome
  "topbar.adminPortal": "بوابة الإدارة",
  "topbar.currentWorkspace": "مساحة العمل الحالية",
  // Mobile hamburger aria-label
  "topbar.openMenu": "فتح القائمة",
  "topbar.closeMenu": "إغلاق القائمة",
  // Auth headings
  "auth.heading.signup": "أنشئ حسابك",
  "auth.heading.verifyPending": "تحقّق من بريدك الإلكتروني",
  "auth.heading.reset": "إعادة تعيين كلمة المرور",
  "auth.heading.resetSent": "تحقّق من بريدك الإلكتروني",
  "auth.heading.login": "مرحبًا بعودتك",
  // Auth subheadings
  "auth.sub.signup": "سجّل للوصول إلى لوحة التحكم وابدأ بتتبّع أرباحك الحقيقية.",
  "auth.sub.reset": "أدخل البريد الإلكتروني المرتبط بحسابك وسنرسل لك رابط إعادة التعيين.",
  "auth.sub.login": "سجّل الدخول للوصول إلى لوحة التحكم ومواصلة إدارة مطعمك.",
  // Auth field labels
  "auth.field.region": "المنطقة",
  "auth.field.name": "الاسم",
  "auth.field.whatsapp": "رقمك",
  "auth.field.businessType": "نوع النشاط التجاري",
  "auth.field.email": "البريد الإلكتروني",
  "auth.field.password": "كلمة المرور",
  // Misc auth copy
  "auth.alreadyHaveAccount": "لديك حساب بالفعل؟ ",
  "auth.newToNetrev": "جديد على Netrev؟ ",
  "auth.keepSignedIn": "أبقِني مسجّلًا للدخول",
  "auth.resetPassword": "إعادة تعيين كلمة المرور",
  "auth.backToHome": "العودة إلى الرئيسية",
  "auth.backToSignIn": "العودة إلى تسجيل الدخول",
  // Primary buttons
  "btn.signIn": "تسجيل الدخول",
  "btn.createAccount": "إنشاء حساب",
  "btn.signOut": "تسجيل الخروج",
  "btn.sendResetLink": "إرسال رابط إعادة التعيين",
  "btn.save": "حفظ",
  "btn.saveItem": "حفظ الصنف",
  "btn.cancel": "إلغاء",
  "btn.continue": "متابعة ←",
  "btn.confirmPay": "التأكيد والدفع عبر Stripe",
  "btn.upgradePro": "الترقية إلى Pro — ‎£49.99‎/شهر",
  "btn.pleaseWait": "يرجى الانتظار…",
  "btn.sending": "جارٍ الإرسال…",
  // Loading / status copy
  "status.settingUpAccount": "جارٍ إعداد حسابك…",
  "status.settingUpSub": "لحظة من فضلك بينما نجهّز مساحة عملك.",
  "status.loadingWorkspaces": "جارٍ تحميل مساحات عملك...",
  // Plan labels
  "plan.free": "مجاني",
  "plan.pro": "Pro",
};
// Merge the per-page catalog modules (full-translation pass) into the base catalogs.
Object.assign(en, landingEn, storyEn, overviewEn, analysisEn, dailylogEn, configEn, subsEn, perfEn, pricetoolEn, appEn, feedEn);
Object.assign(arOverrides, landingAr, storyAr, overviewAr, analysisAr, dailylogAr, configAr, subsAr, perfAr, pricetoolAr, appAr, feedAr);

const ar = Object.fromEntries(
  Object.entries(en).map(([key, value]) => [key, arOverrides[key] || todo(value)])
);

export const catalog = { en, ar };

// ── Language state (localStorage-backed) ─────────────────────────────────
function readLang() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return DEFAULT_LANG;
    const v = window.localStorage.getItem(LANG_KEY);
    return SUPPORTED_LANGS.includes(v) ? v : DEFAULT_LANG;
  } catch (_) {
    return DEFAULT_LANG;
  }
}

export function getLang() {
  return readLang();
}

export function setLang(lang) {
  const next = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      window.localStorage.setItem(LANG_KEY, next);
    }
  } catch (_) { /* ignore quota / private-mode errors */ }
  // Broadcast so useT() consumers re-render in THIS tab (storage event only
  // fires in OTHER tabs). dispatchEvent is a no-op safely if window is absent.
  try {
    if (typeof window !== "undefined" && typeof window.dispatchEvent === "function") {
      window.dispatchEvent(new CustomEvent(LANG_CHANGE_EVENT, { detail: next }));
    }
  } catch (_) { /* ignore */ }
  return next;
}

// Cycle/toggle between English and Arabic. Returns the new language.
export function toggleLang() {
  return setLang(readLang() === "ar" ? "en" : "ar");
}

// ── Direction helpers ────────────────────────────────────────────────────
export function isRTL(lang = readLang()) {
  return lang === "ar";
}

export function dir(lang = readLang()) {
  return isRTL(lang) ? "rtl" : "ltr";
}

// ── Core lookup ──────────────────────────────────────────────────────────
// t(key, lang?) → translated string. Resolution order:
//   1. catalog[lang][key]
//   2. catalog.en[key]   (English fallback — never show a half-localized UI)
//   3. key               (last-resort, so a typo'd key is visible, not blank)
export function t(key, lang = readLang()) {
  const table = catalog[lang] || catalog.en;
  if (table && Object.prototype.hasOwnProperty.call(table, key)) return table[key];
  if (catalog.en && Object.prototype.hasOwnProperty.call(catalog.en, key)) return catalog.en[key];
  return key;
}

// ── React hook ───────────────────────────────────────────────────────────
// useT() returns { t, lang, dir, isRTL, setLang, toggleLang } and re-renders
// the calling component whenever the language changes (same tab via the custom
// event, other tabs via the storage event). The non-React helpers above
// (t/dir/setLang/isRTL) can be imported standalone from plain modules.
export function useT() {
  const [lang, setLangState] = useState(readLang);

  useEffect(() => {
    const sync = () => setLangState(readLang());
    const onStorage = (e) => { if (!e || e.key === LANG_KEY) sync(); };
    if (typeof window !== "undefined") {
      window.addEventListener(LANG_CHANGE_EVENT, sync);
      window.addEventListener("storage", onStorage);
      // Pick up any change that happened between initial render and effect.
      sync();
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener(LANG_CHANGE_EVENT, sync);
        window.removeEventListener("storage", onStorage);
      }
    };
  }, []);

  const boundT = useCallback((key) => t(key, lang), [lang]);

  return {
    t: boundT,
    lang,
    dir: dir(lang),
    isRTL: isRTL(lang),
    setLang,
    toggleLang,
  };
}

export default { t, useT, setLang, toggleLang, getLang, dir, isRTL, catalog, LANG_KEY };
